import { useState, useMemo, useEffect, useRef } from 'react';
import { useAuth } from '../../../utils/Auth';
import API from '../../../API';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from './variants';
import styles from './AssetTracker.module.css';

export default function({qr, id}) {

    const { userObj } = useAuth();
    const { updateQR } = API;

    const inventory = useMemo(() => qr?.asset?.inventory || [], [qr?.asset?.inventory]);

    const containerRef = useRef(null);

    const [ newItem, setNewItem ] = useState(null);
    const [ itemArr, setItemArr ] = useState(inventory);
    const [ loading, setLoading ] = useState(false);
    const [ hasChanges, setHasChanges ] = useState(false);
    
    useEffect(() => {
        setItemArr(inventory);
        setHasChanges(false);
    },[inventory]);

    useEffect(() => {
        setHasChanges(!deepCompare(inventory, itemArr));
    },[itemArr, inventory]);

    const handleUpdateQR = async () => {
        if (userObj) {
            setLoading(true);
            try {
                let apiObj = {
                    qrDataType: 'asset',
                    files: [],
                    data: {
                        ...qr,
                        asset: {
                            ...qr.asset,
                            inventory: itemArr
                        }
                    }
                }
                await updateQR(id, apiObj)
            } catch (err) {
                console.error('Failed to update QR:', err);
            } finally {
                setLoading(false);
                setNewItem(null);
            }
        }
    };

    const handleNewItem = (newItem) => {
        if (userObj) {
            setNewItem(newItem);
            setItemArr(prev => [...prev, newItem]);
        }
    };
    
    useEffect(() => {
        const container = containerRef.current;
        if (container && newItem) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth'
            })
        }
    },[itemArr, newItem]);

    const handleEditName = (e, item) => {
        if (userObj) {
            setItemArr(prev => {
                return prev.map(i => {
                    if (i.id === item.id) {
                        return {
                            ...i,
                            name: e.target.value
                        }
                    }
                    return i
                })
            })
        }
    };

    const handleEditNumber = (e, item) => {
        if (userObj) {
            setItemArr(prev => {
                return prev.map(i => {
                    if (i === item) {
                        return {
                            ...i,
                            value: e.target.value
                        }
                    }
                    return i
                })
            })
        }
    };

    const checkMax = (inputElement) => {
        let value = parseFloat(inputElement.value);
        let max = parseFloat(inputElement.max);

        if (value > max) {
            inputElement.value = max;
        }
    };

    const handleRemoveCounter = async (item) => {
        if (userObj) {
            if (!newItem) {
                setLoading(true);
                setItemArr(prev => {
                    return prev.filter(i => i.id !== item.id);
                });
                try {
                    let apiObj = {
                        qrDataType: 'asset',
                        files: [],
                        data: {
                            ...qr,
                            asset: {
                                ...qr.asset,
                                inventory: itemArr.filter(i => i.id !== item.id)
                            }
                        }
                    }
                    await updateQR(id, apiObj)
                } catch (err) {
                    console.error('Failed to update QR:', err);
                } finally {
                    setLoading(false);
                    setNewItem(null);
                }
            } else {
                setItemArr(prev => {
                    return prev.filter(i => i.id !== item.id);
                });
                setNewItem(null);
            }
        }
    };

    const handleNumberStyle = (value) => {
        if (value.length > 5) {
            return { width: '6.75rem' };
        } else if (value.length > 3) {
            return { width: '5rem' };
        } else return { width: '4rem' };
    };

    const renderItems = useMemo(() => {
        return (
            <AnimatePresence initial={false} mode='wait'>
                {itemArr.length > 0 ? (
                    <>
                    {itemArr.map((item, index) => (
                        <motion.div 
                            key={`item-${index}`}
                            className={styles.inputGroup} 
                            transition={transition}
                            variants={variants}
                            initial='initial'
                            animate='animate'
                            exit='initial'
                        >
                            <input type='text' value={item.name} disabled={!userObj || (newItem !== null && newItem?.id !== item.id)} className={styles.inputText} required onChange={(e) => handleEditName(e, item)} placeholder='Item Name'/>
                            <input type='number' value={item.value} min='0' max='999999' disabled={!userObj || (newItem !== null && newItem?.id !== item.id)} className={styles.inputNumber} style={handleNumberStyle(item.value)} onChange={(e) => handleEditNumber(e, item)} onInput={(e) => checkMax(e.target)} />
                            {userObj && (<button className={styles.cancelBtn} onClick={() => handleRemoveCounter(item)} disabled={loading || !userObj || (newItem !== null && newItem?.id !== item.id)}>{(newItem?.id === item.id) ? <i className='far fa-xmark' /> : <i className='far fa-trash-can' />}</button>)}
                        </motion.div>
                    ))}
                    </>
                ) : (
                    <motion.div 
                        key="no-items-message"
                        className={styles.noItems}
                        transition={transition}
                            variants={variants}
                            initial='initial'
                            animate='animate'
                            exit='initial'
                    >
                        Your inventory is empty.
                    </motion.div>
                )}
            </AnimatePresence>
        )
    },[itemArr, userObj, newItem, loading]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>Inventory</div>
            <div className={styles.body}>
                <div className={styles.content} ref={containerRef}>
                    {renderItems}
                </div>
                {userObj && (
                    <button 
                        className={styles.btn} 
                        disabled={loading}
                        onClick={() => {(newItem || hasChanges) ? handleUpdateQR() : handleNewItem({id: getNewItemId(itemArr), name: '', value: 0})}}
                    >
                        <AnimatePresence initial={false} mode='wait'>
                            {(newItem || hasChanges) ? (
                                <motion.span
                                    key='update'
                                    transition={transition}
                                    variants={variants}
                                    initial='initialText'
                                    animate='animateText'
                                    exit='exitText'
                                >Update QR</motion.span>
                            ): (
                                <motion.span
                                    key='add-item'
                                    transition={transition}
                                    variants={variants}
                                    initial='initialText'
                                    animate='animateText'
                                    exit='exitText'
                                ><i className='far fa-plus' /> Add Item</motion.span>
                            )}
                        </AnimatePresence>
                        {(!loading && hasChanges) && (
                            <div className={styles.hasChangesCircle} />
                        )}
                    </button>
                )}
            </div>
        </div>
    )
};

const deepCompare = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
        const c1 = arr1[i];
        const c2 = arr2[i];
        if (c1.id !== c2.id || c1.name !== c2.name || Number(c1.value) !== Number(c2.value)) {
            return false;
        }
    }
    return true;
};

const getNewItemId = (itemArr) => {
    if (itemArr.length === 0) {
        return 0;
    }

    const maxId = Math.max(...itemArr.map(c => c.id));
    return maxId + 1;
};